import React, { useEffect, useRef, useState } from 'react';
import { Carousel } from 'react-bootstrap';
import ReactImageFallback from 'react-image-fallback';
import { translateComponent } from '@/utils/tolgee/translateComponent';
import { getDateTimeFormated } from '@/components/utils/dates/utils';

import './index.css';
import { LomavisH2 } from '../../typography/LomavisH2';
import { LomavisH1 } from '../../typography/LomavisH1';
import { LomavisShortenedNumber } from '../../utils/numbers/LomavisShortenedNumber';

type MediaFile = {
  id: string;
  url: string;
  preview_image: string;
  file_type: 'image' | 'video' | 'doc' | 'other';
};

type Analytics = {
  comments?: number;
  engagement?: number;
  impressions?: number;
  reach?: number;
  reactions?: number;
  shares?: number;
};

type PostType = 'post' | 'image' | 'multiImage' | 'video';

type PostData = {
  title: string;
  text: string;
  media: MediaFile[];
  postType: PostType;
  type?: string;
  publicationDate: string; // ISO string
  platforms: string[];
  analytics: Analytics;
  permalink?: string;
  profile: {
    name: string;
    imageUrl: string;
  };
};

interface LomavisPostCardDetails1Props {
  /** The text to display inside the H1 heading */
  data: PostData;
}

/**
 * Atoms: LomavisPostCardDetails1
 */
export const LomavisPostCardDetails1: React.FC<
  LomavisPostCardDetails1Props
> = ({ data }) => {
  const t = translateComponent('LomavisPostCardDetails1');

  const [expanded, setExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const textRef = useRef<HTMLSpanElement | null>(null);

  const checkOverflow = () => {
    if (textRef.current) {
      const lineHeight = parseFloat(
        getComputedStyle(textRef.current).lineHeight
      );
      const maxHeight = lineHeight * 3; // height of 3 lines

      setShowToggle(textRef.current.scrollHeight > maxHeight);
    }
  };

  // 🔹 Convert analytics object into array
  const analyticsArray = Object.entries(data.analytics ?? {}).map(
    ([key, value]) => ({
      label: key,
      value: value ?? 0 // default 0 if undefined
    })
  );

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [data?.text]);

  return (
    <div className="d-flex gap-3">
      <div className="w-100 h-100 ">
        <div className="border rounded d-flex flex-column flex-fill w-100 w-xxl-75 mx-auto">
          <div className="p-3 d-flex flex-column gap-2">
            <div className="d-flex justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <ReactImageFallback
                  src={data?.profile.imageUrl}
                  fallbackImage={
                    <div className="d-flex w-100 h-100 align-items-center justify-content-center bg-secondary">
                      <i className="fa-regular fa-user fs-1 text-muted"></i>
                    </div>
                  }
                  alt="profile image"
                  className="w-50px h-50px rounded-circle object-fit-fill"
                />
                <div className="d-flex flex-column gap-1">
                  <span className="fw-bold">{data?.profile?.name}</span>
                  <span className="text-muted fw-bold">
                    {' '}
                    {getDateTimeFormated(data.publicationDate)}
                  </span>
                </div>
              </div>

              <a
                href={data?.permalink} // <-- your external URL
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer"
              >
                <i className="fa-solid fa-arrow-up-right-from-square fs-4"></i>
              </a>
            </div>

            <div>
              {/* Text with ellipsis + toggle */}
              <span ref={textRef} className={expanded ? '' : 'text-ellipsis-3'}>
                {data?.text}{' '}
              </span>

              {/* Toggle link */}
              {showToggle && (
                <span
                  className="text-primary fw-bold cursor-pointer ms-2"
                  onClick={() => setExpanded(!expanded)}
                >
                  {expanded ? t('showLess') : t('readMore')}
                </span>
              )}
            </div>
          </div>

          {/* Carousel Section */}
          {data.media.length > 1 ? (
            <Carousel
              indicators={false}
              interval={null}
              className="w-100"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {data.media.map((item, index) => {
                const isVideo = item?.file_type === 'video';

                return (
                  <Carousel.Item key={index}>
                    {isVideo ? (
                      <video
                        src={item.url}
                        poster={item?.preview_image}
                        controls
                        className="d-block w-100 h-100 border"
                        style={{
                          objectFit: 'contain',
                          maxHeight: '400px',
                          zIndex: 2
                        }}
                      />
                    ) : (
                      <img
                        src={item.url}
                        alt={`Slide ${index}`}
                        className="d-block w-100 h-100 border"
                        style={{
                          objectFit: 'scale-down',
                          maxHeight: '400px'
                        }}
                      />
                    )}
                  </Carousel.Item>
                );
              })}
            </Carousel>
          ) : data.media.length === 1 ? (
            data.media.map((item, index) => {
              const isVideo = item?.file_type === 'video';
              const isReel = data?.type === 'reel';
              return isVideo ? (
                <video
                  key={index}
                  src={item.url}
                  poster={item?.preview_image}
                  controls
                  className="d-block w-100 h-100 border"
                  style={{
                    objectFit: 'contain',
                    maxHeight: '400px',
                    zIndex: 2
                  }}
                />
              ) : (
                <img
                  key={index}
                  src={item.url}
                  onClick={() => {
                    if (isReel && data?.permalink) {
                      window.open(
                        data.permalink,
                        '_blank',
                        'noopener,noreferrer'
                      );
                    }
                  }}
                  alt={`Media ${index}`}
                  className={`d-block w-100 h-100 border ${
                    isReel && 'cursor-pointer'
                  }`}
                  style={{
                    objectFit: 'scale-down',
                    maxHeight: '400px'
                  }}
                />
              );
            })
          ) : (
            <div></div>
          )}

          <div className="w-100 p-2 row">
            <div className="justify-content-center col-4 d-flex align-items-center gap-1">
              <i className="fa-regular fa-thumbs-up fs-3"></i>
              <span className="text-muted fw-bold">{t('like')}</span>
            </div>
            <div className="justify-content-center col-4 d-flex align-items-center gap-1">
              <i className="fa-regular fa-message fs-3"></i>
              <span className="text-muted fw-bold">{t('comment')}</span>
            </div>
            <div className=" col-4 justify-content-center d-flex align-items-center gap-1">
              <i className="fa-regular fa-share fs-3"></i>
              <span className="text-muted fw-bold">{t('share')}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex flex-column flex-fill w-100">
        <span className="py-2 border-bottom">
          <LomavisH2 title={t('postAnalytics')} />
        </span>
        <div className="mt-2 border rounded p-2">
          <div className="row">
            {analyticsArray?.map((item, index) => (
              <div key={item?.label} className="col-6 mb-2">
                <div className="d-flex flex-column gap-1 p-2">
                  <span className="text-muted fw-bold fs-5">
                    {t(item?.label)}
                  </span>
                  {item?.value === 0 ? (
                    <LomavisH1 title={'-'} />
                  ) : (
                    <h1 className="fw-bolder fs-1">
                      <LomavisShortenedNumber value={item?.value} />{' '}
                    </h1>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <span className="text-muted mt-2 fw-medium">
          * "-" {t('data_not_available')}
        </span>
      </div>
    </div>
  );
};
